import axios from "axios";
import { bumpVendors, bumpCchs } from "@/lib/masterDataBus";
import { normalizeEntry } from "@/lib/dayKind";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------- Vendors ----------
export const vendorApi = {
  list: () => axios.get(`${API}/vendors`).then((r) => r.data),
  // iter148 — every write bumps the shared bus so Hand Receipts + Reports
  // + Fund Utilization pages auto-refresh in the same tab and all other
  // open tabs of the same origin.
  upsert: async (vendor, id) => {
    const data = await axios.post(`${API}/vendors${id ? `?id=${id}` : ""}`, vendor).then((r) => r.data);
    bumpVendors();
    return data;
  },
  remove: async (id) => {
    const data = await axios.delete(`${API}/vendors/${id}`).then((r) => r.data);
    bumpVendors();
    return data;
  },
};

// ---------- CCHs ----------
export const cchApi = {
  list: () => axios.get(`${API}/cchs`).then((r) => r.data),
  upsert: async (cch, id) => {
    const data = await axios.post(`${API}/cchs${id ? `?id=${id}` : ""}`, cch).then((r) => r.data);
    bumpCchs();
    return data;
  },
  remove: async (id) => {
    const data = await axios.delete(`${API}/cchs/${id}`).then((r) => r.data);
    bumpCchs();
    return data;
  },
};

// ---------- Daily Entries ----------
export const dailyApi = {
  list: ({ schoolId, fromDate, toDate } = {}) => {
    const params = {};
    if (schoolId) params.schoolId = schoolId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return axios.get(`${API}/daily-entries`, { params }).then((r) => (r.data || []).map(normalizeEntry));
  },
  upsert: (entry, id) => axios.post(`${API}/daily-entries${id ? `?id=${id}` : ""}`, entry).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/daily-entries/${id}`).then((r) => r.data),
  // iter131 — bulk-delete every entry for one (schoolId, year, month). The
  // backend refuses when the month is hard-locked and returns `{count}` so
  // the UI can confirm exactly how many rows were wiped.
  resetMonth: ({ schoolId, year, month }) =>
    axios.post(`${API}/daily-entries/reset-month`, { schoolId, year, month }).then((r) => r.data),
  // iter132 — wipe only a `[fromDate, toDate]` window. Same hard-lock guard
  // as reset-month, but lets schools fix a few problem days without losing
  // the rest of the month.
  resetRange: ({ schoolId, fromDate, toDate }) =>
    axios.post(`${API}/daily-entries/reset-range`, { schoolId, fromDate, toDate }).then((r) => r.data),
};

// ---------- MDCF (Monthly Data Capture Format) ----------
export const mdcfApi = {
  get: (schoolId, year, month) =>
    axios.get(`${API}/mdcf/${schoolId}/${year}/${month}`).then((r) => r.data),
  upsert: (payload) => axios.post(`${API}/mdcf`, payload).then((r) => r.data),
  remove: (schoolId, year, month) =>
    axios.delete(`${API}/mdcf/${schoolId}/${year}/${month}`).then((r) => r.data),
};

// ---------- Fund Balances (monthly opening / received / closing) ----------
export const fundApi = {
  list: (schoolId) =>
    axios.get(`${API}/fund-balances`, { params: { schoolId } }).then((r) => r.data),
  get: (schoolId, year, month) =>
    axios.get(`${API}/fund-balances/${schoolId}/${year}/${month}`).then((r) => r.data),
  upsert: (payload) => axios.post(`${API}/fund-balances`, payload).then((r) => r.data),
  remove: (schoolId, year, month) =>
    axios.delete(`${API}/fund-balances/${schoolId}/${year}/${month}`).then((r) => r.data),
  // iter155 — dated rice receipts
  splitRice: (payload) =>
    axios.post(`${API}/fund-balances/split-rice`, payload).then((r) => r.data),
  addReceipt: (payload) =>
    axios.post(`${API}/fund-balances/receipt`, payload).then((r) => r.data),
  undoReceipt: (payload) =>
    axios.post(`${API}/fund-balances/receipt/undo`, payload).then((r) => r.data),
};

// ---------- Generator State (Quarterly UC scratchpad — schoolInfo + vendorInfo + months[]) ----------
export const generatorStateApi = {
  get: (schoolId, year, quarter) =>
    axios.get(`${API}/generator-states/${schoolId}/${year}/${quarter}`).then((r) => r.data),
  upsert: (payload) => axios.post(`${API}/generator-states`, payload).then((r) => r.data),
  remove: (schoolId, year, quarter) =>
    axios.delete(`${API}/generator-states/${schoolId}/${year}/${quarter}`).then((r) => r.data),
};

// ---------- Year-wise Enrollment (per-FY snapshot of class-bucket headcounts) ----------
// Each record is keyed by (schoolId, financialYear) — e.g. "2025-26". Saving an
// edit also appends an entry to `school_enrollment_history` so the user can
// audit who/when/what changed across the year.
export const enrollmentApi = {
  list: (schoolId) =>
    axios.get(`${API}/school-enrollments`, { params: { schoolId } }).then((r) => r.data),
  get: (schoolId, financialYear) =>
    axios.get(`${API}/school-enrollments/${schoolId}/${financialYear}`).then((r) => r.data),
  upsert: (schoolId, financialYear, payload) =>
    axios.put(`${API}/school-enrollments/${schoolId}/${financialYear}`, payload).then((r) => r.data),
  remove: (schoolId, financialYear) =>
    axios.delete(`${API}/school-enrollments/${schoolId}/${financialYear}`).then((r) => r.data),
  history: (schoolId, financialYear) =>
    axios.get(`${API}/school-enrollment-history`, {
      params: financialYear ? { schoolId, financialYear } : { schoolId },
    }).then((r) => r.data),
};

// FY helper — Indian financial year runs April → March. Months 4-12 of `year`
// belong to FY `${year}-${(year+1)%100}`; months 1-3 belong to FY
// `${year-1}-${year%100}`. Used by MonthlyMDCF + Setup to derive the FY
// of the currently-selected month.
export function financialYearFromMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  const start = m >= 4 ? y : y - 1;
  const end = (start + 1) % 100;
  return `${start}-${String(end).padStart(2, "0")}`;
}

